import { MenuItem, Select } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Material,
  MaterialAddRequestMaterialTypeEnum,
} from "../../../services";
import apiClient from "../../../utils/api";
import { ThumbnailViewer } from "./ThumbnailViewer";

interface ClassMaterialsInfo {
  id: string;
  fileName: string;
  file: string;
  materialType: string;
  availableDate: Date;
}

export const ClassPage: React.FC = () => {
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [classMaterials, setClassMaterials] = React.useState<
    ClassMaterialsInfo[]
  >([]);
  const [materialType, setMaterialType] =
    React.useState<MaterialAddRequestMaterialTypeEnum>(
      MaterialAddRequestMaterialTypeEnum.ClassMaterials
    );
  const [availableDate, setAvailableDate] = React.useState<Date>(new Date());
  const location = useLocation();
  const classID = location.pathname.split("/").pop() || "";
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const addMaterialPost = async () => {
    try {
      const res = await apiClient.materialControllerAdd({
        materialAddRequest: {
          classId: classID,
          materialType: materialType,
          availableDate: availableDate,
        },
      });
      if (res) {
        setOpenSnackbar(true);
        fetchMaterialList();
      }
    } catch (error) {
      console.log("error adding class material", error);
    }
  };
  const fetchMaterialList = async () => {
    try {
      const { materials } = await apiClient.materialControllerList({
        materialListRequest: { classId: classID },
      });
      fetchMaterialInfoArray(materials).then((materialInfoArray) => {
        setClassMaterials(materialInfoArray);
      });
    } catch (error) {
      console.log("error fetching class material", error);
    }
  };

  React.useEffect(() => {
    const fetchMaterialInfoArray = async (
      materials: Material[]
    ): Promise<ClassMaterialsInfo[]> => {
      return Promise.all(materials.map(convertToMaterialInfo));
    };
    const fetchMaterialList = async () => {
      try {
        const { materials } = await apiClient.materialControllerList({
          materialListRequest: { classId: classID },
        });
        fetchMaterialInfoArray(materials).then((materialInfoArray) => {
          setClassMaterials(materialInfoArray);
        });
      } catch (error) {
        console.log("error fetching class material", error);
      }
    };
    fetchMaterialList();
  }, [classID]); // empty dependency array to run the effect only once

  const fetchMaterialInfoArray = async (
    materials: Material[]
  ): Promise<ClassMaterialsInfo[]> => {
    return Promise.all(materials.map(convertToMaterialInfo));
  };

  const convertToMaterialInfo = async (
    material: Material
  ): Promise<ClassMaterialsInfo> => {
    try {
      const { documents } =
        await apiClient.materialControllerGetListByMaterialId({
          materialID: material.id,
        });
      return {
        id: material.id,
        fileName: documents[0].fileName,
        file: documents[0].file,
        materialType: material.materialType,
        availableDate: material.availableDate,
      };
    } catch (error) {
      console.error("Error fetching material data:", error);
      return {
        id: material.id,
        fileName: "",
        file: "",
        materialType: material.materialType,
        availableDate: material.availableDate,
      }; // Return a default object in case of an error
    }
  };

  return (
    <>
      <h1> Welcome to the class page</h1>
      <div className="class-container">
        <div className="add-pdf">
          <h3>Please click the link below to create material for class.</h3>
          <h4>
            Choose the material type and adjust date the material will be made
            available to students before clicking.
          </h4>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginLeft: "10px",
            }}
          >
            <Select
              value={materialType}
              onChange={(e) =>
                setMaterialType(
                  e.target.value as MaterialAddRequestMaterialTypeEnum
                )
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Material Type
              </MenuItem>
              <MenuItem
                value={MaterialAddRequestMaterialTypeEnum.ClassMaterials}
              >
                Lecture PDF
              </MenuItem>
              <MenuItem value={MaterialAddRequestMaterialTypeEnum.Recitation}>
                Recitation
              </MenuItem>
              <MenuItem
                value={MaterialAddRequestMaterialTypeEnum.RecitationAnswer}
              >
                Recitation Answer
              </MenuItem>
            </Select>
            <label>Available Date:</label>
            <input
              type="date"
              value={availableDate.toISOString().split("T")[0]}
              onChange={(e) => setAvailableDate(new Date(e.target.value))}
            />
            <Button
              component="label"
              variant="contained"
              onClick={addMaterialPost}
            >
              Add material
            </Button>
          </div>
        </div>
        <div className="class-materials">
          <h3>*View your class materials below*</h3>
          <Grid container columns={{ xs: 4, sm: 8, md: 12 }}>
            {classMaterials &&
              classMaterials.map((material, index) => {
                // console.log(classMaterials);
                return (
                  <Grid item xs={2} sm={4} md={4} key={index}>
                    <div style={{ textAlign: "center" }}>
                      <Link
                        to={`/admin/${classID}/${material.id}`}
                        target="_blank"
                      >
                        <ThumbnailViewer pdfFile={material.file} />
                        <h4>Type: {material.materialType}</h4>
                        <h4>Main File Name: {material.fileName}</h4>
                        <h4>
                          Available Date:{" "}
                          {material.availableDate.toDateString()}
                        </h4>
                        <Button component="label" variant="contained">
                          Upload Document
                        </Button>
                      </Link>
                    </div>
                  </Grid>
                );
              })}
          </Grid>
        </div>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleSnackbarClose}
          severity="success"
        >
          New class material added successfully!
        </MuiAlert>
      </Snackbar>
    </>
  );
};
